import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useNFCReader } from '@/hooks/useNFCReader';
import { useAuth } from '@/contexts/AuthContext';
import { useScannerContext } from '@/contexts/ScannerContext';
import { api } from '@/lib/apiClient';
import { Package, Calendar, Nfc, Building2, FolderOpen, Wrench, AlertTriangle, Check, ChevronsUpDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { PlotterCut } from '@/types/legacy';
import { PrintLabelDialog } from '@/components/Labels/PrintLabelDialog';
import { FreezerMaterialSelector, type SelectedMaterial } from './FreezerMaterialSelector';

// Material con trazabilidad integrada
interface PlotterCutMaterialWithTraceability {
  type: string;
  layers: number;
  gsm?: number;
  batch_number?: string;
  roll_id?: string;
  supplier?: string;
  expiration_date?: string;
  raw_material_id?: string;
  raw_material_name?: string;
}


const formSchema = z.object({
  package_name: z.string().min(3, 'Minimo 3 caracteres'),
  dimensions: z.string().optional(),
  weight_grams: z.number().optional(),
  area_m2: z.number().optional(),
  square_meters_consumed: z.number().optional(),
  linear_meters_cut: z.number().optional(),
  roll_width_cm: z.number().optional(),
  cut_date: z.string(),
  location: z.string().optional(),
  notes: z.string().optional(),
  assigned_work_order_id: z.string().optional(), // Optional - can be empty for test cuts
  assigned_product_model_id: z.string().optional(), // Modelo de producto seleccionado
  nfc_tag_uid: z.string().optional(),
  material_inventory_id: z.string().optional(),
  material_lot_id: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface PlotterCutFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  editingCut?: PlotterCut | null;
}

const materialTypes = [
  { value: 'carbon_fiber', label: 'Fibra de Carbono' },
  { value: 'fiberglass', label: 'Fibra de Vidrio' },
  { value: 'kevlar', label: 'Kevlar' },
  { value: 'hybrid', label: 'Hibrido' },
  { value: 'prepreg', label: 'Prepreg' },
  { value: 'dry_fiber', label: 'Fibra Seca' },
  { value: 'other', label: 'Otro' },
];

interface WorkOrderOption {
  id: string;
  order_number: string;
  product_name: string;
  customer?: string;
  project_name?: string;
  part_id?: string;
  mold_id?: string;
  workflow_type?: string;
  mold?: {
    id: string;
    name: string;
    nfc_tag_uid?: string;
  } | null;
}

interface NFCTagOption {
  tag_uid: string;
  tag_label: string | null;
}

export function PlotterCutForm({ open, onOpenChange, onSuccess, editingCut }: PlotterCutFormProps) {
  const { user, profile } = useAuth();
  const { isSupported, writeToTag, scanTagUid, isScanning } = useNFCReader();
  const [scanningMobile, setScanningMobile] = useState(false);
  const { nfcBridge, writeTag: writeBridgeTag } = useScannerContext();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [savedPlotterCut, setSavedPlotterCut] = useState<{id: string; package_code: string; package_name: string} | null>(null);
  const [materials, setMaterials] = useState<PlotterCutMaterialWithTraceability[]>([
    { type: 'carbon_fiber', layers: 1 }
  ]);
  const [writeTagOnSave, setWriteTagOnSave] = useState(true);
  const [regenerateQr, setRegenerateQr] = useState(true);
  const [detectedNewTag, setDetectedNewTag] = useState<string | null>(null);
  const [withoutWorkOrder, setWithoutWorkOrder] = useState(false);
  const [lastUsbReadTimestamp, setLastUsbReadTimestamp] = useState<number>(0);

  const { data: workOrders = [] } = useQuery({
    queryKey: ['plotter-work-orders'],
    queryFn: async () => {
      const { data, error } = await api
        .from('work_orders')
        .select('id, order_number, product_name, customer, part_id, mold_id, workflow_type, mold:molds(id, name, nfc_tag_uid), project:projects(name)')
        .in('status', ['pending', 'in_progress'])
        .order('order_number');
      if (error) throw error;
      return (data || []).map((wo: any) => ({
        ...wo,
        project_name: wo.project?.name || null,
        mold: wo.mold || null,
      })) as WorkOrderOption[];
    },
    enabled: open,
  });

  // Query para modelos de producto
  const { data: productModels = [] } = useQuery({
    queryKey: ['product-models-plotter'],
    queryFn: async () => {
      const { data } = await api
        .from('product_models')
        .select('id,name,reference,product_number')
        .eq('is_active', true)
        .order('name');
      return data || [];
    },
  });

  // Estado para modo manual de nombre
  const [manualNameMode, setManualNameMode] = useState(false);

  // Estado para el combobox de modelo de producto
  const [productModelOpen, setProductModelOpen] = useState(false);
  const [productModelSearch, setProductModelSearch] = useState('');

  // Memoria de cortes repetitivos
  const LAST_CUT_KEY = 'athenea_last_plotter_cut';
  const [showPreloadedWarning, setShowPreloadedWarning] = useState(false);
  const [preloadedFromMemory, setPreloadedFromMemory] = useState(false);

  // State for selected freezer materials (multiple)
  const [selectedFreezerMaterials, setSelectedFreezerMaterials] = useState<Array<{
    material: SelectedMaterial;
    linearMetersCut: number;
    rollWidthCm: number;
    squareMetersConsumed: number;
    layers: number;
  }>>([]);

  // Legacy single material support (for backwards compatibility)
  const [selectedFreezerMaterial, setSelectedFreezerMaterial] = useState<SelectedMaterial | null>(null);
  const [linearMetersCut, setLinearMetersCut] = useState<number>(0);
  const [rollWidthCm, setRollWidthCm] = useState<number>(100);

  // Add new material slot
  const addMaterialSlot = () => {
    setSelectedFreezerMaterials(prev => [...prev, {
      material: null as any,
      linearMetersCut: 0,
      rollWidthCm: 100,
      squareMetersConsumed: 0,
      layers: 1,
    }]);
  };

  // Remove material slot
  const removeMaterialSlot = (index: number) => {
    setSelectedFreezerMaterials(prev => prev.filter((_, i) => i !== index));
  };

  // Update material slot
  const updateMaterialSlot = (index: number, updates: Partial<typeof selectedFreezerMaterials[0]>) => {
    setSelectedFreezerMaterials(prev => prev.map((slot, i) =>
      i === index ? { ...slot, ...updates } : slot
    ));
  };

  // Calculate totals
  const getTotalSquareMeters = () => {
    return selectedFreezerMaterials.reduce((sum, m) => sum + (m.squareMetersConsumed || 0), 0);
  };

  const getTotalLayersFromMaterials = () => {
    return selectedFreezerMaterials.reduce((sum, m) => sum + (m.layers || 0), 0);
  };

  const { data: availableTags = [] } = useQuery({
    queryKey: ['plotter-nfc-tags', open, editingCut?.nfc_tag_uid],
    queryFn: async () => {
      const { data, error } = await api
        .from('nfc_tags')
        .select('tag_uid, tag_label, work_order_id, process_id')
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      if (error) throw error;
      const raw = (data || []) as any[];
      return raw.filter((tag) => {
        const isFree = !tag.work_order_id && !tag.process_id;
        const isCurrent = editingCut?.nfc_tag_uid && tag.tag_uid === editingCut.nfc_tag_uid;
        return isFree || isCurrent;
      }) as NFCTagOption[];
    },
    enabled: open,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      cut_date: new Date().toISOString().split('T')[0],
      assigned_work_order_id: '',
      nfc_tag_uid: '',
    },
  });

  // Get selected work order details (must be after useForm)
  const selectedWorkOrder = workOrders.find(wo => wo.id === watch('assigned_work_order_id'));

  // Suscribirse a cambios del campo NFC para forzar re-renders cuando se escanea
  const nfcTagUidValue = watch('nfc_tag_uid');

  useEffect(() => {
    if (editingCut) {
      reset({
        package_name: editingCut.package_name,
        dimensions: editingCut.dimensions,
        weight_grams: editingCut.weight_grams,
        area_m2: editingCut.area_m2,
        cut_date: editingCut.cut_date,
        location: editingCut.location,
        notes: editingCut.notes,
        assigned_work_order_id: editingCut.assigned_work_order_id || '',
        nfc_tag_uid: editingCut.nfc_tag_uid || '',
      });
      if (editingCut.materials && editingCut.materials.length > 0) {
        setMaterials(editingCut.materials);
      }
    } else {
      // Intentar cargar datos del último corte guardado
      const savedCut = localStorage.getItem(LAST_CUT_KEY);
      if (savedCut && open) {
        try {
          const parsed = JSON.parse(savedCut);
          reset({
            cut_date: new Date().toISOString().split('T')[0],
            assigned_work_order_id: '',
            nfc_tag_uid: '',
            package_name: '',
            assigned_product_model_id: parsed.assigned_product_model_id || '',
            dimensions: parsed.dimensions || '',
            weight_grams: parsed.weight_grams || undefined,
            area_m2: parsed.area_m2 || undefined,
            location: parsed.location || '',
          });
          if (parsed.materials && parsed.materials.length > 0) {
            setMaterials(parsed.materials);
          } else {
            setMaterials([{ type: 'carbon_fiber', layers: 1 }]);
          }
          setPreloadedFromMemory(true);
          setShowPreloadedWarning(true);
        } catch {
          reset({
            cut_date: new Date().toISOString().split('T')[0],
            assigned_work_order_id: '',
            nfc_tag_uid: '',
          });
          setMaterials([{ type: 'carbon_fiber', layers: 1 }]);
          setPreloadedFromMemory(false);
          setShowPreloadedWarning(false);
        }
      } else {
        reset({
          cut_date: new Date().toISOString().split('T')[0],
          assigned_work_order_id: '',
          nfc_tag_uid: '',
        });
        setMaterials([{ type: 'carbon_fiber', layers: 1 }]);
        setPreloadedFromMemory(false);
        setShowPreloadedWarning(false);
      }
    }
    // NO resetear detectedNewTag aquí - se hace en useEffect separado
    setWithoutWorkOrder(false);
    setSelectedFreezerMaterial(null);
    setSelectedFreezerMaterials([]);
  }, [editingCut, reset, open]);

  // Reset NFC solo cuando el modal se cierra (evita borrar UID recién escaneado)
  useEffect(() => {
    if (!open) {
      setDetectedNewTag(null);
      setLastUsbReadTimestamp(0);
    }
  }, [open]);

  // Detectar chips NFC nuevos desde el lector del PC
  useEffect(() => {
    if (!open) return;
    if (!nfcBridge.lastRead) return;

    const now = Date.now();
    const readAge = now - nfcBridge.lastRead.timestamp;

    // Debug logging
    console.log('[NFC PlotterCutForm] lastRead:', nfcBridge.lastRead, 'age:', readAge, 'lastTimestamp:', lastUsbReadTimestamp);

    // Ignorar lecturas de hace más de 5 segundos (para evitar procesar lecturas viejas al abrir)
    if (readAge > 5000) {
      console.log('[NFC PlotterCutForm] Skipping - read too old:', readAge, 'ms');
      return;
    }

    // Evitar procesar la misma lectura dos veces
    if (nfcBridge.lastRead.timestamp <= lastUsbReadTimestamp) {
      console.log('[NFC PlotterCutForm] Skipping - already processed');
      return;
    }

    const detectedUid = nfcBridge.lastRead.uid?.trim().toUpperCase();
    if (!detectedUid) {
      console.log('[NFC PlotterCutForm] No UID in lastRead');
      return;
    }

    // Guardar timestamp ANTES de hacer cualquier cambio de estado
    const newTimestamp = nfcBridge.lastRead.timestamp;
    setLastUsbReadTimestamp(newTimestamp);

    // Comprobar si ya está en la lista de etiquetas disponibles
    const existsInList = availableTags.some(tag => tag.tag_uid?.toUpperCase() === detectedUid);

    if (existsInList) {
      // Si ya existe, seleccionarla directamente
      setValue('nfc_tag_uid', detectedUid);
      setDetectedNewTag(null);
      toast({
        title: 'Etiqueta detectada',
        description: `Seleccionada: ${detectedUid}`,
      });
    } else {
      // Es un chip nuevo, marcarlo y asignarlo
      setDetectedNewTag(detectedUid);
      setValue('nfc_tag_uid', detectedUid, { shouldValidate: true, shouldDirty: true });
      toast({
        title: 'Nuevo chip NFC detectado',
        description: `UID: ${detectedUid} - Se registrará al guardar`,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nfcBridge.lastRead?.timestamp, open]);

  const getTotalLayers = () => materials.reduce((sum, m) => sum + (m.layers || 0), 0);

  const generateQRData = (packageCode: string, data: FormData) => {
    return JSON.stringify({
      code: packageCode,
      name: data.package_name,
      layers: getTotalLayers(),
      cut_date: data.cut_date,
      materials: materials,
    });
  };

  const buildTagPayload = (packageCode: string, data: FormData, tagUid?: string | null) => {
    const assignedWorkOrder = workOrders.find((wo) => wo.id === data.assigned_work_order_id);

    return {
      tagType: "plotter_cut",
      tagUid: tagUid || undefined,
      package: {
        code: packageCode,
        name: data.package_name,
        total_layers: getTotalLayers(),
        dimensions: data.dimensions || null,
        weight_grams: data.weight_grams || null,
        area_m2: data.area_m2 || null,
        linear_meters: data.linear_meters_cut || null,
        roll_width_cm: data.roll_width_cm || null,
        cut_date: data.cut_date,
        materials: materials,
      },
      materials_used: selectedFreezerMaterials.length > 0
        ? selectedFreezerMaterials.filter(s => s.material).map(s => ({
            id: s.material.material.id,
            lot_id: s.material.lot.id,
            name: s.material.material.name,
            batch: s.material.lot.batch_number || null,
            roll: s.material.lot.roll_number || null,
            expiry: s.material.lot.expiry_date || null,
            layers: s.layers,
            sqm: s.squareMetersConsumed,
          }))
        : null,
      work_order: assignedWorkOrder
        ? {
            id: assignedWorkOrder.id,
            order_number: assignedWorkOrder.order_number,
            product_name: assignedWorkOrder.product_name,
            customer: assignedWorkOrder.customer || null,
            project: assignedWorkOrder.project_name || null,
            part_id: assignedWorkOrder.part_id || null,
          }
        : null,
      mold: assignedWorkOrder?.mold
        ? {
            id: assignedWorkOrder.mold.id,
            name: assignedWorkOrder.mold.name,
            nfc_uid: assignedWorkOrder.mold.nfc_tag_uid || null,
          }
        : null,
    };
  };

  const onSubmit = async (data: FormData) => {
    if (!user) return;

    if (materials.length === 0) {
      toast({
        title: 'Error',
        description: 'Agrega al menos un material',
        variant: 'destructive',
      });
      return;
    }

    // Only require OT if not in "without work order" mode
    if (!editingCut && !withoutWorkOrder && !data.assigned_work_order_id) {
      toast({
        title: 'Orden de trabajo requerida',
        description: 'Selecciona la orden de trabajo para vincular la etiqueta NFC',
        variant: 'destructive',
      });
      return;
    }

    // Aviso si no hay NFC (opcional pero recomendado)
    if (!editingCut && !data.nfc_tag_uid) {
      toast({
        title: 'Paquete sin NFC',
        description: 'Recuerda agregar una etiqueta NFC después para vincular a órdenes de trabajo',
      });
    }

    setLoading(true);
    try {
      const assignedWorkOrderId = data.assigned_work_order_id || null;
      // OTs de ámbito plotter (solo corte): al congelar el paquete, la OT se
      // completa y el paquete queda LIBRE en el pool del congelador para que
      // una futura OT de laminado lo consuma. Por eso no se asigna al paquete.
      const assignedWO = workOrders.find((wo) => wo.id === assignedWorkOrderId);
      const isPlotterScopeWO = assignedWO?.workflow_type === 'plotter_first' || assignedWO?.workflow_type === 'plotter_only';
      const previousTagUid = editingCut?.nfc_tag_uid || null;
      const tagUid = data.nfc_tag_uid || null;
      const totalLayers = getTotalLayers();
      const primaryMaterialType = materials[0]?.type || 'carbon_fiber';
      const updatedQrData = editingCut && regenerateQr
        ? generateQRData(editingCut.package_code, data)
        : null;
      let firstProcess: { id: string; name: string; sequence_order: number } | null = null;

      if (tagUid) {
        const { data: processData, error: processError } = await api
          .from('manufacturing_processes')
          .select('id, name, sequence_order')
          .order('sequence_order')
          .limit(1)
          .single();
        if (processError) throw processError;
        firstProcess = processData;

        // Si es un chip nuevo detectado, registrarlo primero
        if (detectedNewTag === tagUid) {
          const { error: insertTagError } = await api
            .from('nfc_tags')
            .insert({
              tag_uid: tagUid,
              tag_label: null,
              description: 'Registrado desde Corte Plotter',
              status: 'active',
              created_by: user.id,
            });
          if (insertTagError && !insertTagError.message?.includes('duplicate')) {
            throw insertTagError;
          }
        }
      }

      if (editingCut) {
        // Actualizar
        const { error } = await api
          .from('plotter_cuts')
          .update({
            package_name: data.package_name,
            materials: materials,
            primary_material: materialTypes.find(t => t.value === primaryMaterialType)?.label || primaryMaterialType,
            material_type: primaryMaterialType,
            total_layers: totalLayers,
            dimensions: data.dimensions,
            weight_grams: data.weight_grams,
            area_m2: data.area_m2,
            cut_date: data.cut_date,
            location: data.location,
            notes: data.notes,
            assigned_work_order_id: assignedWorkOrderId,
            nfc_tag_uid: tagUid,
            ...(updatedQrData ? { qr_code: updatedQrData, qr_generated_at: new Date().toISOString() } : {}),
          })
          .eq('id', editingCut.id);

        if (error) throw error;
        if (tagUid) {
          const tagPayload = buildTagPayload(editingCut.package_code, data, tagUid);
          const { error: tagError } = await api
            .from('nfc_tags')
            .update({
              tag_label: editingCut.package_code,
              description: data.package_name,
              tag_type: 'plotter_cut',
              process_id: firstProcess?.id || null,
              work_order_id: assignedWorkOrderId,
              location: data.location || null,
              notes: JSON.stringify(tagPayload),
              assigned_at: new Date().toISOString(),
              assigned_by: user.id,
            })
            .eq('tag_uid', tagUid);
          if (tagError) {
            console.error('Error updating nfc_tags:', tagError);
            toast({ title: 'Advertencia', description: 'No se pudo actualizar el registro NFC, pero el paquete fue guardado' });
          }
        }
        if (previousTagUid && previousTagUid !== tagUid) {
          await api
            .from('nfc_tags')
            .update({
              work_order_id: null,
              process_id: null,
              tag_type: null,
              assigned_at: null,
              assigned_by: null,
            })
            .eq('tag_uid', previousTagUid);
        }
        if (tagUid && writeTagOnSave) {
          const payload = buildTagPayload(editingCut.package_code, data, tagUid);
          // Intentar escribir via bridge USB primero
          if (nfcBridge.isReaderReady) {
            try {
              const success = await writeBridgeTag(payload);
              if (success) {
                toast({ title: 'NFC escrito', description: 'Datos grabados en la etiqueta (USB)' });
              }
            } catch (error: any) {
              toast({ title: 'Error NFC USB', description: error?.message || 'No se pudo escribir', variant: 'destructive' });
            }
          } else if (isSupported) {
            // Fallback a NFC móvil
            try {
              await writeToTag(payload);
              toast({ title: 'NFC escrito', description: 'Datos grabados en la etiqueta (móvil)' });
            } catch (error: any) {
              toast({ title: 'Error NFC', description: error?.message || 'No se pudo escribir', variant: 'destructive' });
            }
          } else {
            toast({ title: 'NFC no disponible', description: 'Conecta un lector USB o usa un móvil con NFC' });
          }
        }
        toast({ title: 'Paquete actualizado', description: 'Los cambios han sido guardados' });
      } else {
        // Crear nuevo
        const { data: codeResult } = await api.rpc('generate_package_code');
        const packageCode = codeResult || `PKG-${Date.now()}`;

        const qrData = generateQRData(packageCode, data);

        // Prepare insert data with new freezer material fields
        const insertData: Record<string, any> = {
          package_code: packageCode,
          package_name: data.package_name,
          materials: materials,
          primary_material: materialTypes.find(t => t.value === primaryMaterialType)?.label || primaryMaterialType,
          material_type: primaryMaterialType,
          total_layers: totalLayers,
          dimensions: data.dimensions,
          weight_grams: data.weight_grams,
          area_m2: data.area_m2,
          cut_date: data.cut_date,
          location: data.location,
          notes: data.notes,
          qr_code: qrData,
          qr_generated_at: new Date().toISOString(),
          // Ámbito plotter: paquete libre en el pool; se guarda la OT de origen para trazabilidad
          assigned_work_order_id: isPlotterScopeWO ? null : assignedWorkOrderId,
          origin_work_order_id: assignedWorkOrderId,
          nfc_tag_uid: tagUid,
          created_by: user.id,
          created_by_name: profile?.full_name || user.email,
          status: 'available',
          is_test_cut: withoutWorkOrder, // Flag for cuts without work order
        };

        // Add freezer material fields - use first material for backwards compatibility
        if (selectedFreezerMaterials.length > 0 && selectedFreezerMaterials[0].material) {
          const firstSlot = selectedFreezerMaterials[0];
          insertData.material_inventory_id = firstSlot.material.material.id;
          insertData.material_lot_id = firstSlot.material.lot.id;
          insertData.linear_meters_cut = firstSlot.linearMetersCut || null;
          insertData.roll_width_cm = firstSlot.rollWidthCm || null;
          insertData.square_meters_consumed = getTotalSquareMeters() || null;
        }

        const { data: insertedCut, error } = await api
          .from('plotter_cuts')
          .insert(insertData)
          .select('id')
          .single();

        if (error) throw error;

        // Si hay OT asignada, vincular el paquete y crear proceso de corte completado
        if (assignedWorkOrderId && insertedCut?.id) {
          // 1. Vincular paquete a la OT
          await api
            .from('work_orders')
            .update({ linked_plotter_cut_id: insertedCut.id })
            .eq('id', assignedWorkOrderId);

          // 2. Obtener el proceso PLOT (Corte)
          const { data: plotProcess } = await api
            .from('manufacturing_processes')
            .select('id')
            .eq('code', 'PLOT')
            .single();

          // 3. Crear registro de proceso de corte completado
          if (plotProcess?.id) {
            await api
              .from('process_records')
              .insert({
                work_order_id: assignedWorkOrderId,
                process_id: plotProcess.id,
                operator_id: user.id,
                status: 'completed',
                started_at: new Date().toISOString(),
                completed_at: new Date().toISOString(),
                notes: `Paquete ${packageCode} creado con ${totalLayers} capas`,
                process_data: {
                  plotter_cut_id: insertedCut.id,
                  package_code: packageCode,
                  package_name: data.package_name,
                  nfc_tag_uid: tagUid || null,
                  materials: materials,
                },
              });

            if (isPlotterScopeWO) {
              // OT de solo corte: completar también material_cutting y cerrar la OT.
              // El paquete queda disponible en el pool del congelador.
              const { data: cuttingProcess } = await api
                .from('manufacturing_processes')
                .select('id')
                .eq('code', 'material_cutting')
                .single();

              if (cuttingProcess?.id) {
                const { data: existingRecords } = await api
                  .from('process_records')
                  .select('id,status')
                  .eq('work_order_id', assignedWorkOrderId)
                  .eq('process_id', cuttingProcess.id);
                const pendingRecord = (existingRecords || []).find((r: any) => r.status !== 'completed');
                if (pendingRecord) {
                  await api
                    .from('process_records')
                    .update({
                      status: 'completed',
                      completed_at: new Date().toISOString(),
                      operator_id: user.id,
                    })
                    .eq('id', pendingRecord.id);
                } else if (!existingRecords || existingRecords.length === 0) {
                  await api.from('process_records').insert({
                    work_order_id: assignedWorkOrderId,
                    process_id: cuttingProcess.id,
                    operator_id: user.id,
                    status: 'completed',
                    started_at: new Date().toISOString(),
                    completed_at: new Date().toISOString(),
                    notes: `Corte completado - paquete ${packageCode}`,
                  });
                }
              }

              await api
                .from('work_orders')
                .update({
                  status: 'completed',
                  completed_at: new Date().toISOString(),
                })
                .eq('id', assignedWorkOrderId);
            } else {
              // 4. Actualizar estado de la OT a in_progress si estaba pending
              await api
                .from('work_orders')
                .update({
                  status: 'in_progress',
                  started_at: new Date().toISOString(),
                })
                .eq('id', assignedWorkOrderId)
                .eq('status', 'pending');
            }
          }

          if (isPlotterScopeWO) {
            toast({
              title: 'OT de corte completada',
              description: `Paquete ${packageCode} disponible en el pool del congelador para laminado - OT ${workOrders.find(wo => wo.id === assignedWorkOrderId)?.order_number} cerrada`,
            });
          } else {
            toast({
              title: 'Paquete vinculado a OT',
              description: `Listo para laminado - OT: ${workOrders.find(wo => wo.id === assignedWorkOrderId)?.order_number}`,
            });
          }
        }

        if (tagUid) {
          const tagPayload = buildTagPayload(packageCode, data, tagUid);
          const { error: tagError } = await api
            .from('nfc_tags')
            .update({
              tag_label: packageCode,
              description: data.package_name,
              tag_type: 'plotter_cut',
              process_id: firstProcess?.id || null,
              work_order_id: assignedWorkOrderId,
              location: data.location || null,
              notes: JSON.stringify(tagPayload),
              assigned_at: new Date().toISOString(),
              assigned_by: user.id,
            })
            .eq('tag_uid', tagUid);
          if (tagError) {
            console.error('Error updating nfc_tags:', tagError);
            toast({ title: 'Advertencia', description: 'No se pudo actualizar el registro NFC, pero el paquete fue guardado' });
          }
        }
        if (tagUid && writeTagOnSave) {
          const payload = buildTagPayload(packageCode, data, tagUid);
          // Intentar escribir via bridge USB primero
          if (nfcBridge.isReaderReady) {
            try {
              const success = await writeBridgeTag(payload);
              if (success) {
                toast({ title: 'NFC escrito', description: 'Datos grabados en la etiqueta (USB)' });
              }
            } catch (error: any) {
              toast({ title: 'Error NFC USB', description: error?.message || 'No se pudo escribir', variant: 'destructive' });
            }
          } else if (isSupported) {
            // Fallback a NFC móvil
            try {
              await writeToTag(payload);
              toast({ title: 'NFC escrito', description: 'Datos grabados en la etiqueta (móvil)' });
            } catch (error: any) {
              toast({ title: 'Error NFC', description: error?.message || 'No se pudo escribir', variant: 'destructive' });
            }
          } else {
            toast({ title: 'NFC no disponible', description: 'Conecta un lector USB o usa un móvil con NFC' });
          }
        }
        // Deduct square meters from all selected freezer materials
        if (selectedFreezerMaterials.length > 0) {
          for (const slot of selectedFreezerMaterials) {
            if (!slot.material || slot.squareMetersConsumed <= 0) continue;

            try {
              const available = slot.material.availableSquareMeters;
              if (available < slot.squareMetersConsumed) {
                toast({
                  title: 'Stock insuficiente',
                  description: `Material ${slot.material.material.name}: Disponible ${available.toFixed(2)} m², requerido ${slot.squareMetersConsumed.toFixed(2)} m²`,
                  variant: 'destructive',
                });
                continue;
              }

              // Update lot's square_meters_used
              const currentUsed = slot.material.lot.square_meters_used || 0;
              const { error: lotUpdateError } = await api
                .from('material_lots')
                .update({
                  square_meters_used: currentUsed + slot.squareMetersConsumed,
                })
                .eq('id', slot.material.lot.id);
              if (lotUpdateError) throw lotUpdateError;

              // Create inventory movement record
              await api.from('inventory_movements').insert({
                material_inventory_id: slot.material.material.id,
                movement_type: 'out',
                quantity: slot.squareMetersConsumed,
                reason: 'Corte plotter',
                reference_type: 'plotter_cut',
                performed_by: user.id,
                notes: `Paquete ${packageCode} - ${slot.material.material.name} - Lote ${slot.material.lot.batch_number || 'N/A'}${slot.material.lot.roll_number ? ` / Rollo ${slot.material.lot.roll_number}` : ''} - ${slot.layers} capas`,
              });
            } catch (deductErr) {
              console.error('Deduction error for material:', slot.material.material.name, deductErr);
            }
          }

          const totalMaterials = selectedFreezerMaterials.filter(s => s.material && s.squareMetersConsumed > 0).length;
          if (totalMaterials > 0) {
            toast({
              title: 'Materiales descontados',
              description: `Descontado de ${totalMaterials} material${totalMaterials > 1 ? 'es' : ''} (${getTotalSquareMeters().toFixed(2)} m² total)`,
            });
          }
        }
        toast({ title: 'Paquete creado', description: `Codigo: ${packageCode}` });

        // Guardar datos del corte en memoria para repetir
        localStorage.setItem(LAST_CUT_KEY, JSON.stringify({
          assigned_product_model_id: data.assigned_product_model_id,
          materials: materials,
          dimensions: data.dimensions,
          weight_grams: data.weight_grams,
          area_m2: data.area_m2,
          location: data.location,
          savedAt: new Date().toISOString(),
        }));

        // Save cut data for print dialog
        if (insertedCut?.id) {
          setSavedPlotterCut({
            id: insertedCut.id,
            package_code: packageCode,
            package_name: data.package_name,
          });
          setShowPrintDialog(true);
        }
      }

      onSuccess();
      // Don't close immediately for new packages - let user decide to print or skip
      if (editingCut) {
        onOpenChange(false);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar el paquete',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            {editingCut ? 'Editar Paquete' : 'Nuevo Paquete de Corte Plotter'}
          </DialogTitle>
        </DialogHeader>

        {/* Aviso de datos precargados desde memoria */}
        {showPreloadedWarning && preloadedFromMemory && !editingCut && (
          <div className="rounded-lg border border-amber-500 bg-amber-50 dark:bg-amber-950/20 p-4 space-y-2">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Datos precargados del último corte</span>
            </div>
            <p className="text-sm text-amber-600 dark:text-amber-300">
              Revisa los campos y edita los que necesites antes de proceder. Los datos de materiales, dimensiones y área se han cargado del corte anterior.
            </p>
            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowPreloadedWarning(false);
                }}
                className="border-amber-500 text-amber-700 hover:bg-amber-100"
              >
                Entendido, revisar campos
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  localStorage.removeItem(LAST_CUT_KEY);
                  reset({
                    cut_date: new Date().toISOString().split('T')[0],
                    assigned_work_order_id: '',
                    nfc_tag_uid: '',
                  });
                  setMaterials([{ type: 'carbon_fiber', layers: 1 }]);
                  setPreloadedFromMemory(false);
                  setShowPreloadedWarning(false);
                }}
                className="text-muted-foreground"
              >
                Limpiar y empezar desde cero
              </Button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {editingCut && (
            <div className="flex items-center justify-between rounded-lg border p-3 text-sm">
              <span>Regenerar QR al guardar cambios</span>
              <Switch checked={regenerateQr} onCheckedChange={setRegenerateQr} />
            </div>
          )}

          {/* 1. ORDEN DE TRABAJO - Primer paso, prominente */}
          <Card className={cn("border-2", withoutWorkOrder ? "border-amber-500/50" : "border-primary/50")}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FolderOpen className={cn("w-5 h-5", withoutWorkOrder ? "text-amber-500" : "text-primary")} />
                1. Orden de Trabajo {!withoutWorkOrder && "*"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Checkbox para corte sin OT */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="without-ot"
                  checked={withoutWorkOrder}
                  onCheckedChange={(checked) => {
                    setWithoutWorkOrder(checked as boolean);
                    if (checked) {
                      setValue('assigned_work_order_id', '');
                    }
                  }}
                />
                <Label htmlFor="without-ot" className="text-sm cursor-pointer">
                  Corte sin OT (excepcional/pruebas)
                </Label>
              </div>

              {/* Advertencia modo prueba */}
              {withoutWorkOrder && (
                <div className="p-3 bg-amber-500/10 border border-amber-300 rounded-md text-sm text-amber-700 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    <strong>MODO PRUEBA:</strong> Este corte no estará asociado a ninguna orden de trabajo.
                    Solo usar para pruebas de material o cortes excepcionales.
                  </span>
                </div>
              )}

              {/* Selector de OT */}
              <Select
                value={watch('assigned_work_order_id') || ""}
                onValueChange={(value) => setValue('assigned_work_order_id', value)}
                disabled={withoutWorkOrder}
              >
                <SelectTrigger className={cn(
                  selectedWorkOrder ? "border-primary" : "",
                  withoutWorkOrder && "opacity-50"
                )}>
                  <SelectValue placeholder={withoutWorkOrder ? "No aplica (modo prueba)" : "Seleccionar orden de trabajo..."} />
                </SelectTrigger>
                <SelectContent>
                  {workOrders.filter(wo => wo.id && wo.id.trim() !== '').map((wo) => (
                    <SelectItem key={wo.id} value={wo.id}>
                      {wo.order_number} - {wo.product_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!withoutWorkOrder && errors.assigned_work_order_id && (
                <p className="text-sm text-destructive">{errors.assigned_work_order_id.message}</p>
              )}

              {/* Detalles de la OT seleccionada */}
              {selectedWorkOrder && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Building2 className="w-3 h-3" />
                      Cliente
                    </div>
                    <p className="text-sm font-medium">{selectedWorkOrder.customer || 'Sin cliente'}</p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <FolderOpen className="w-3 h-3" />
                      Proyecto
                    </div>
                    <p className="text-sm font-medium">{selectedWorkOrder.project_name || 'Sin proyecto'}</p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Package className="w-3 h-3" />
                      Pieza / Producto
                    </div>
                    <p className="text-sm font-medium">
                      {selectedWorkOrder.part_id && <Badge variant="outline" className="mr-2">{selectedWorkOrder.part_id}</Badge>}
                      {selectedWorkOrder.product_name}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Wrench className="w-3 h-3" />
                      Molde Asignado
                    </div>
                    {selectedWorkOrder.mold ? (
                      <p className="text-sm font-medium">
                        {selectedWorkOrder.mold.name}
                        {selectedWorkOrder.mold.nfc_tag_uid && (
                          <Badge variant="secondary" className="ml-2 text-xs font-mono">
                            NFC: {selectedWorkOrder.mold.nfc_tag_uid.slice(0, 8)}...
                          </Badge>
                        )}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">Sin molde asignado</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* 2. MATERIALES DE CONGELADOR (múltiples) */}
          {(selectedWorkOrder || withoutWorkOrder || editingCut) && (
            <Card className="border-2 border-blue-500/30">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-500" />
                    2. Materiales del Paquete
                    {selectedFreezerMaterials.length > 0 && (
                      <Badge variant="secondary">{selectedFreezerMaterials.length} material{selectedFreezerMaterials.length > 1 ? 'es' : ''}</Badge>
                    )}
                  </CardTitle>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addMaterialSlot}
                    className="gap-1"
                  >
                    <span className="text-lg leading-none">+</span> Agregar Material
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedFreezerMaterials.length === 0 ? (
                  <div className="text-center py-6 border-2 border-dashed rounded-lg">
                    <Package className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No hay materiales agregados</p>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      onClick={addMaterialSlot}
                    >
                      + Agregar primer material
                    </Button>
                  </div>
                ) : (
                  <>
                    {selectedFreezerMaterials.map((slot, index) => (
                      <div key={index} className="border rounded-lg p-4 space-y-4 bg-muted/30">
                        <div className="flex items-center justify-between">
                          <Label className="font-semibold">Material {index + 1}</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeMaterialSlot(index)}
                            className="text-destructive hover:text-destructive"
                          >
                            ✕ Quitar
                          </Button>
                        </div>

                        {/* Selector de material */}
                        <FreezerMaterialSelector
                          value={slot.material}
                          onChange={(selection) => {
                            if (selection) {
                              updateMaterialSlot(index, { material: selection });
                              // Update materials array
                              const newMaterials = [...materials];
                              if (!newMaterials[index]) {
                                newMaterials[index] = { type: 'carbon_fiber', layers: 1 };
                              }
                              newMaterials[index].raw_material_id = selection.material.id;
                              newMaterials[index].raw_material_name = selection.material.name;
                              newMaterials[index].batch_number = selection.lot.batch_number || undefined;
                              newMaterials[index].roll_id = selection.lot.roll_number || undefined;
                              newMaterials[index].expiration_date = selection.lot.expiry_date || undefined;
                              newMaterials[index].supplier = selection.material.supplier || undefined;
                              setMaterials(newMaterials);
                            }
                          }}
                          disabled={false}
                        />

                        {/* Datos de corte por material */}
                        {slot.material && (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t">
                            <div className="space-y-1">
                              <Label className="text-xs">Metros Lineales</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="2.5"
                                value={slot.linearMetersCut || ''}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  const area = val * (slot.rollWidthCm / 100);
                                  updateMaterialSlot(index, {
                                    linearMetersCut: val,
                                    squareMetersConsumed: Math.round(area * 100) / 100,
                                  });
                                }}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Ancho (cm)</Label>
                              <Input
                                type="number"
                                step="1"
                                min="1"
                                placeholder="100"
                                value={slot.rollWidthCm || ''}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value) || 100;
                                  const area = slot.linearMetersCut * (val / 100);
                                  updateMaterialSlot(index, {
                                    rollWidthCm: val,
                                    squareMetersConsumed: Math.round(area * 100) / 100,
                                  });
                                }}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Área (m²)</Label>
                              <Input
                                type="number"
                                className="bg-muted"
                                readOnly
                                value={slot.squareMetersConsumed || ''}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Capas</Label>
                              <Input
                                type="number"
                                min="1"
                                value={slot.layers || 1}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 1;
                                  updateMaterialSlot(index, { layers: val });
                                  // Update materials array
                                  const newMaterials = [...materials];
                                  if (newMaterials[index]) {
                                    newMaterials[index].layers = val;
                                    setMaterials(newMaterials);
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Stock check per material */}
                        {slot.material && slot.squareMetersConsumed > 0 && (
                          <div className={cn(
                            "p-2 rounded text-xs",
                            slot.squareMetersConsumed <= slot.material.availableSquareMeters
                              ? "bg-green-500/10 text-green-700"
                              : "bg-red-500/10 text-red-700"
                          )}>
                            {slot.squareMetersConsumed <= slot.material.availableSquareMeters
                              ? `✓ OK: ${slot.material.availableSquareMeters.toFixed(2)} m² disponibles`
                              : `⚠ Insuficiente: Solo ${slot.material.availableSquareMeters.toFixed(2)} m²`}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Totales */}
                    {selectedFreezerMaterials.length > 1 && (
                      <div className="flex justify-between items-center p-3 bg-primary/10 rounded-lg font-medium">
                        <span>TOTALES:</span>
                        <div className="flex gap-4 text-sm">
                          <span>{getTotalLayersFromMaterials()} capas</span>
                          <span>{getTotalSquareMeters().toFixed(2)} m²</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* 3. DATOS DEL CORTE */}
          {(selectedFreezerMaterials.length > 0 || editingCut) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">3. Datos del Paquete</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Nombre del paquete */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="package_name">Nombre del Paquete *</Label>
                    <div className="flex items-center gap-2 mb-2">
                      <Switch
                        checked={manualNameMode}
                        onCheckedChange={setManualNameMode}
                        id="manual-mode"
                      />
                      <Label htmlFor="manual-mode" className="text-xs text-muted-foreground cursor-pointer">
                        Nombre manual (prueba/excepcional)
                      </Label>
                    </div>
                    {manualNameMode ? (
                      <Input
                        id="package_name"
                        placeholder="Ej: Kit Capo BMW M3 - 6 capas"
                        {...register('package_name')}
                      />
                    ) : (
                      <Popover open={productModelOpen} onOpenChange={setProductModelOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={productModelOpen}
                            className="w-full justify-between font-normal"
                          >
                            {watch('assigned_product_model_id')
                              ? (() => {
                                  const model = productModels.find((m: any) => m.id === watch('assigned_product_model_id'));
                                  return model ? `${model.reference ? `${model.reference} - ` : ''}${model.name}` : 'Seleccionar modelo...';
                                })()
                              : 'Seleccionar modelo de producto...'}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command shouldFilter={false}>
                            <CommandInput
                              placeholder="Buscar por nombre o referencia..."
                              value={productModelSearch}
                              onValueChange={setProductModelSearch}
                            />
                            <CommandList>
                              <CommandEmpty>No se encontraron modelos.</CommandEmpty>
                              <CommandGroup>
                                {productModels
                                  .filter((model: any) => {
                                    if (!productModelSearch) return true;
                                    const search = productModelSearch.toLowerCase();
                                    return (
                                      String(model.name || '').toLowerCase().includes(search) ||
                                      String(model.reference || '').toLowerCase().includes(search) ||
                                      String(model.product_number || '').toLowerCase().includes(search)
                                    );
                                  })
                                  .slice(0, 50)
                                  .map((model: any) => (
                                    <CommandItem
                                      key={model.id}
                                      value={model.id}
                                      onSelect={() => {
                                        setValue('assigned_product_model_id', model.id);
                                        setValue('package_name', model.name || '');
                                        setProductModelOpen(false);
                                        setProductModelSearch('');
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          watch('assigned_product_model_id') === model.id ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      <div className="flex flex-col">
                                        <span>{model.reference ? `${model.reference} - ` : ''}{model.name}</span>
                                        {model.product_number && (
                                          <span className="text-xs text-muted-foreground">PN: {model.product_number}</span>
                                        )}
                                      </div>
                                    </CommandItem>
                                  ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    )}
                    {errors.package_name && (
                      <p className="text-sm text-destructive">{errors.package_name.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Total Capas</Label>
                    <Input
                      type="number"
                      className="bg-muted font-semibold"
                      readOnly
                      value={getTotalLayersFromMaterials() || getTotalLayers()}
                    />
                    <p className="text-xs text-muted-foreground">Suma de capas de todos los materiales</p>
                  </div>
                </div>

                {/* Dimensiones y peso */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Dimensiones (pieza)</Label>
                    <Input
                      placeholder="500x300mm"
                      {...register('dimensions')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Peso (g)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="250"
                      {...register('weight_grams', { valueAsNumber: true })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Área Total (m²)</Label>
                    <Input
                      type="number"
                      className="bg-muted font-semibold"
                      readOnly
                      value={getTotalSquareMeters().toFixed(2)}
                    />
                  </div>
                </div>

                {/* Resumen de materiales */}
                {selectedFreezerMaterials.length > 0 && (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <Label className="text-xs text-muted-foreground mb-2 block">Resumen de Materiales</Label>
                    <div className="space-y-1 text-sm">
                      {selectedFreezerMaterials.map((slot, i) => slot.material && (
                        <div key={i} className="flex justify-between">
                          <span>{slot.material.material.name} - {slot.material.lot.batch_number || 'N/A'}</span>
                          <span className="text-muted-foreground">{slot.layers} capas / {slot.squareMetersConsumed.toFixed(2)} m²</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* 4. Fecha, Ubicación y NFC */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Nfc className="w-5 h-5" />
                4. Fecha, Ubicación y NFC
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Fecha y ubicación */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cut_date">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Fecha de Corte *
                  </Label>
                  <Input
                    id="cut_date"
                    type="date"
                    {...register('cut_date')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ubicación</Label>
                  <Input
                    placeholder="Congelador A - Estante 3"
                    {...register('location')}
                  />
                </div>
              </div>

              {/* NFC */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Nfc className="w-4 h-4" />
                    Etiqueta NFC
                    {nfcBridge.isReaderReady && (
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                    )}
                  </Label>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Escribir al guardar</span>
                    <Switch
                      checked={writeTagOnSave}
                      onCheckedChange={setWriteTagOnSave}
                      disabled={!isSupported && !nfcBridge.isReaderReady}
                    />
                  </div>
                </div>

                {/* Selector de etiqueta existente */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Seleccionar etiqueta existente</Label>
                  <Select
                    value={detectedNewTag || nfcTagUidValue || "none"}
                    onValueChange={(value) => {
                      const uid = value === "none" ? "" : value;
                      setValue('nfc_tag_uid', uid, { shouldValidate: true });
                      // Solo limpiar detectedNewTag si selecciona una etiqueta diferente
                      if (uid && uid !== detectedNewTag) {
                        setDetectedNewTag(null);
                      } else if (uid === detectedNewTag) {
                        // Mantener detectedNewTag si selecciona el mismo
                      } else if (!uid) {
                        setDetectedNewTag(null);
                      }
                    }}
                  >
                    <SelectTrigger className={detectedNewTag ? "border-green-500 ring-1 ring-green-500" : ""}>
                      <SelectValue placeholder="Seleccionar etiqueta registrada..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin etiqueta</SelectItem>
                      {detectedNewTag && (
                        <SelectItem value={detectedNewTag} className="text-green-600 font-medium">
                          🆕 Nuevo: {detectedNewTag}
                        </SelectItem>
                      )}
                      {availableTags.filter(tag => tag.tag_uid && tag.tag_uid.trim() !== '').map((tag) => (
                        <SelectItem key={tag.tag_uid} value={tag.tag_uid}>
                          {tag.tag_label ? `${tag.tag_label} (${tag.tag_uid})` : tag.tag_uid}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Nueva etiqueta - siempre visible */}
                <div className="p-3 border-2 border-dashed border-primary/30 rounded-lg bg-primary/5 space-y-3">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs">+</span>
                    Registrar Nueva Etiqueta
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Escanea una etiqueta NFC en blanco o ingresa su UID manualmente. Se registrará y escribirá con los datos del paquete.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="UID de la etiqueta (ej: 04:A3:B2:C1:D0:E9:F8)"
                      value={detectedNewTag || nfcTagUidValue || ''}
                      onChange={(e) => {
                        const uid = e.target.value.toUpperCase();
                        setDetectedNewTag(uid || null);
                        setValue('nfc_tag_uid', uid || '');
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pasted = e.clipboardData.getData('text').toUpperCase().trim();
                        if (pasted) {
                          setDetectedNewTag(pasted);
                          setValue('nfc_tag_uid', pasted);
                          toast({ title: 'UID pegado', description: pasted });
                        }
                      }}
                      className={cn(
                        "font-mono text-sm",
                        detectedNewTag && "border-green-500 ring-1 ring-green-500"
                      )}
                    />
                    {nfcBridge.isReaderReady && (
                      <Button
                        type="button"
                        variant="default"
                        size="default"
                        className="shrink-0 bg-green-600 hover:bg-green-700"
                        onClick={() => {
                          const uid = nfcBridge.lastRead?.uid;
                          console.log('[NFC Capturar] lastRead:', nfcBridge.lastRead, 'uid:', uid);
                          if (uid) {
                            // Forzar actualización inmediata
                            const uidClean = uid.trim().toUpperCase();
                            setDetectedNewTag(uidClean);
                            setValue('nfc_tag_uid', uidClean, { shouldValidate: true, shouldDirty: true });
                            // También actualizar el input directamente por si acaso
                            const inputEl = document.querySelector('input[placeholder*="UID de la etiqueta"]') as HTMLInputElement;
                            if (inputEl) {
                              inputEl.value = uidClean;
                            }
                            toast({
                              title: 'UID capturado',
                              description: uidClean,
                            });
                          } else {
                            toast({
                              title: 'Sin lectura reciente',
                              description: 'Acerca una etiqueta NFC al lector y vuelve a intentar',
                              variant: 'destructive',
                            });
                          }
                        }}
                      >
                        <Nfc className="w-4 h-4 mr-2" />
                        Capturar
                      </Button>
                    )}
                    {isSupported && (
                      <Button
                        type="button"
                        variant="outline"
                        size="default"
                        disabled={scanningMobile || isScanning}
                        className="shrink-0"
                        onClick={async () => {
                          setScanningMobile(true);
                          const result = await scanTagUid();
                          setScanningMobile(false);
                          if (result?.serialNumber) {
                            const exists = availableTags.some(t => t.tag_uid === result.serialNumber);
                            if (exists) {
                              setValue('nfc_tag_uid', result.serialNumber);
                              setDetectedNewTag(null);
                              toast({ title: 'Etiqueta existente', description: `Seleccionada: ${result.serialNumber}` });
                            } else {
                              setDetectedNewTag(result.serialNumber);
                              setValue('nfc_tag_uid', result.serialNumber);
                              toast({ title: 'Nueva etiqueta detectada', description: `UID: ${result.serialNumber}` });
                            }
                          }
                        }}
                      >
                        <Nfc className="w-4 h-4 mr-2" />
                        {scanningMobile ? 'Escaneando...' : 'Móvil'}
                      </Button>
                    )}
                  </div>
                  {detectedNewTag && (
                    <div className="flex items-center gap-2 p-2 bg-green-500/10 border border-green-300 rounded text-sm text-green-700">
                      <Nfc className="w-4 h-4" />
                      <span>
                        <strong>Nueva etiqueta:</strong> {detectedNewTag}
                        <br />
                        <span className="text-xs">Se registrará y escribirá automáticamente al crear el paquete.</span>
                      </span>
                    </div>
                  )}
                </div>

                {!editingCut && availableTags.length === 0 && !detectedNewTag && !nfcBridge.isReaderReady && !isSupported && (
                  <p className="text-xs text-muted-foreground">
                    Conecta un lector NFC o usa un dispositivo móvil con NFC para escanear etiquetas.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Notas */}
          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea
              placeholder="Observaciones adicionales..."
              rows={2}
              {...register('notes')}
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="bg-gradient-magma">
              {loading ? 'Guardando...' : editingCut ? 'Guardar Cambios' : 'Crear Paquete'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>

      {/* Print Label Dialog */}
      <PrintLabelDialog
        open={showPrintDialog}
        onOpenChange={(open) => {
          setShowPrintDialog(open);
          if (!open && savedPlotterCut) {
            onOpenChange(false);
            setSavedPlotterCut(null);
          }
        }}
        labelType="plotter"
        entityId={savedPlotterCut?.id || ''}
        entityName={savedPlotterCut ? `${savedPlotterCut.package_code} - ${savedPlotterCut.package_name}` : ""}
        plotterCut={savedPlotterCut ? {
          id: savedPlotterCut.id,
          package_code: savedPlotterCut.package_code,
          package_name: savedPlotterCut.package_name,
          cut_date: savedPlotterCut.cut_date,
          materials: savedPlotterCut.materials || materials,
          total_layers: savedPlotterCut.total_layers || getTotalLayers(),
          created_by_name: savedPlotterCut.created_by_name || profile?.full_name || user?.email,
        } : undefined}
        onPrintComplete={() => {
          setShowPrintDialog(false);
          onOpenChange(false);
          setSavedPlotterCut(null);
        }}
      />
    </>
  );
}







